// Shared PowerSync local-DB read helpers.
//
// The real `AbstractPowerSyncDatabase` (@powersync/common) DOES expose
// `getOptional`; several connector paths feature-detect it anyway so execute-only
// unit-test stubs (which omit it) degrade to their INSERT path. This module owns
// that cast + detection ONCE so the three former inline copies
// (connector-quarantine.ts ×2, connector-session-rehome-registry.ts ×1) cannot
// drift (audit-1 #8).
//
// CRITICAL — these helpers do NOT swallow query errors. A SELECT failure must
// propagate so the caller's batch retries cleanly. The earlier inline copies
// wrapped the read in `catch { return false }`; on a batch retry where the row
// was ALREADY inserted, a transient read failure routed the caller to the
// INSERT branch, which then PK-conflicts on the existing row, throws, and
// WEDGES the upload queue these paths exist to drain (audit-1 #1). Swallowing
// is the caller's choice (enrichment reads may legitimately swallow); the
// existence-check callers must not.

import type { PowerSyncWriteDb } from './powersync-db'

type GetOptionalFn = (sql: string, params: unknown[]) => Promise<unknown>

/**
 * Run `db.getOptional(sql, params)` when the SDK exposes it. Returns the row,
 * or `null` when (a) the method is unavailable — execute-only unit-test stubs
 * fall here, so callers degrade to their INSERT path — or (b) the query found
 * no row. Does NOT catch query errors: a genuine read failure propagates so the
 * caller decides (propagate to retry, or swallow for additive enrichment).
 */
export async function getOptionalRow(
  db: PowerSyncWriteDb,
  sql: string,
  params: unknown[],
): Promise<unknown | null> {
  const getOptional = (db as { getOptional?: GetOptionalFn }).getOptional
  if (typeof getOptional !== 'function') return null
  const row = await getOptional.call(db, sql, params)
  return row ?? null
}

/**
 * True when an existence-check query (`SELECT id … WHERE id = ? LIMIT 1`)
 * matches a row. Returns `false` when the SDK lacks `getOptional` (execute-only
 * stubs → INSERT path). PROPAGATES a query error rather than swallowing it: a
 * transient read failure must NOT route a re-quarantine / re-record to an
 * INSERT that PK-conflicts and wedges the upload queue (audit-1 #1). The row is
 * already preserved from the prior run, so a thrown batch simply retries.
 */
export async function powerSyncRowExists(
  db: PowerSyncWriteDb,
  sql: string,
  params: unknown[],
): Promise<boolean> {
  return (await getOptionalRow(db, sql, params)) != null
}
